/**
 * src/DesktopCommandCenter.js
 *
 * Authenticated VISINVOKE product portal for the Desktop Command Centre.
 *
 * AUDIENCE: a signed-in VISINVOKE user (route is behind PrivateRoute). In-product page, not a
 * public landing page: no account-creation, no signup CTAs, no marketing-to-visitors copy.
 *
 * STRUCTURE (locked — do not reorder):
 *   1. Compact hero (identity).
 *   2. TOP HERO CARD (two columns, side by side):
 *        LEFT  = download button, version, a strong DCC description + benefits, "what happens next".
 *        RIGHT = Quick installation guide: steps + the (guide-sized) SmartScreen screenshot.
 *   3. FEATURE CARDS below — three dense product/access sections.
 *
 * This revision is a VISUAL pass only (type scale, spacing, density, balance) on the approved
 * structure. Nothing functional changed: installer file, download URL (COMMAND_CENTER_DOWNLOAD_URL),
 * onDownload behaviour (record the download event then navigate to the installer), authentication
 * and onboarding logic are all identical.
 */
import React from 'react';
import { glassSurface } from './config/glassTheme';
import ProductNav from './components/ProductNav';
import API from './api';
import { useAuth } from './AuthContext';
import { DCC_VERSION, DCC_INSTALL_STEPS, DCC_SMARTSCREEN_IMG, DCC_SMARTSCREEN_CAPTION } from './dccInstallGuide';

// VISINVOKE-hosted installer, served from the existing visinvoke-portal S3 bucket behind the
// existing app.visinvoke.com CloudFront distribution (EPPBRA1BCH37F) under /downloads/.
// UNCHANGED download URL.
const COMMAND_CENTER_DOWNLOAD_URL = 'https://app.visinvoke.com/downloads/VISINVOKE-Desktop-Command-Centre.exe';

// Three dense product/access sections (below the top hero card).
const PROVIDES = [
  {
    accent: '#34d399',
    tag: 'Included with your account',
    title: 'VISINVOKE Utilities',
    intro: 'Full-version VISINVOKE utilities and PC resources, opened straight from your desktop — no browser required.',
    points: [
      'Full-version VISINVOKE utilities — not trials or demos',
      'Desktop access to Windows help and error-code guidance',
      'Support resources and references gathered in one place',
    ],
  },
  {
    accent: '#38bdf8',
    tag: 'Technician Access',
    title: 'Technician Resources',
    intro: 'Professional repair tooling for technicians working on real client machines, from any PC.',
    points: [
      'Technician tools and the Tech Hub catalogue',
      'Professional references and official supplier links',
      'Repair, setup and diagnostic workflows for client jobs',
    ],
  },
  {
    accent: '#a855f7',
    tag: 'Account-based',
    title: 'VISINVOKE Services',
    intro: 'Secure, account-based access to connected VISINVOKE features — growing as the platform expands.',
    points: [
      'Secure access to connected VISINVOKE services',
      'Features tied to your VISINVOKE account and access level',
      'New services added as the VISINVOKE platform expands',
    ],
  },
];

export default function DesktopCommandCenter() {
  const { refreshUser } = useAuth();
  const downloadReady = !!COMMAND_CENTER_DOWNLOAD_URL;

  // UNCHANGED download behaviour.
  const onDownload = async () => {
    if (downloadReady) {
      try { await API.post('/v1/auth/dcc-downloaded'); await refreshUser(); } catch (e) {}
      window.location.href = COMMAND_CENTER_DOWNLOAD_URL;
    }
  };

  return (
    <div style={g.page}>
      <ProductNav />

      <div style={g.wrap}>

        {/* 1. Compact hero */}
        <div style={g.hero}>
          <div style={g.badge}><span style={g.badgeIcon}>{'\u{1F5A5}'}</span><span>VISINVOKE Desktop</span></div>
          <h1 style={g.title}>Desktop Command Centre</h1>
          <p style={g.tagline}>The desktop gateway for VISINVOKE utilities, resources and services.</p>
        </div>

        {/* 2. TOP HERO CARD — download (left) + installation guide (right) */}
        <div style={g.topBand}>
          <div style={g.bandGlow} />

          {/* LEFT: download + version + description + what happens next */}
          <div style={g.dlCol}>
            <div style={g.kicker}>Get the app</div>
            <div style={g.versionRow}>
              <span style={g.versionPill}>Version {DCC_VERSION}</span>
              <span style={g.versionMeta}>Windows 10 &amp; 11 · 64-bit</span>
            </div>
            <button
              style={{ ...g.ctaButton, ...(downloadReady ? {} : g.ctaDisabled) }}
              onClick={onDownload}
              disabled={!downloadReady}
              onMouseEnter={e => { if (downloadReady) e.currentTarget.style.filter = 'brightness(1.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.filter = ''; }}
            >
              {downloadReady ? 'Download Desktop Command Centre' : 'Download Coming Soon'}
            </button>
            <div style={g.trustLine}>Published by VISINVOKE · downloaded directly from app.visinvoke.com</div>

            <p style={g.lead}>
              Desktop Command Centre is your VISINVOKE workspace on Windows — one secure desktop app that
              brings your utilities, technician resources and connected services together. Install it once
              and reach everything tied to your account straight from your desktop.
            </p>

            <div style={g.nextTitle}>What happens next</div>
            <div style={g.nextList}>
              <div style={g.nextItem}><span style={g.nextNum}>1</span><span>The installer downloads to your PC.</span></div>
              <div style={g.nextItem}><span style={g.nextNum}>2</span><span>Open it and follow the quick installation guide.</span></div>
              <div style={g.nextItem}><span style={g.nextNum}>3</span><span>Sign in with the VISINVOKE account you are using now.</span></div>
            </div>
          </div>

          {/* RIGHT: quick installation guide — steps + guide screenshot */}
          <div style={g.instCol}>
            <div style={g.kicker}>Installation</div>
            <div style={g.instHeading}>Quick installation guide</div>
            <ol style={g.steps}>
              {DCC_INSTALL_STEPS.map((step, i) => <li key={i} style={g.step}>{step}</li>)}
            </ol>
            <div style={g.instHelp}>
              <img
                src={DCC_SMARTSCREEN_IMG}
                alt="Windows installation confirmation — select More info, then Run anyway"
                style={g.shot}
              />
              <p style={g.caption}>{DCC_SMARTSCREEN_CAPTION}</p>
            </div>
          </div>
        </div>

        {/* 3. FEATURE CARDS — three dense product/access sections */}
        <div style={g.sectionHead}>What Desktop Command Centre provides</div>
        <div style={g.provideGrid}>
          {PROVIDES.map((p, i) => (
            <div key={i} style={g.provideCard}>
              <div style={g.provideTop}>
                <span style={{ ...g.provideDot, background: p.accent }} />
                <span style={g.provideTitle}>{p.title}</span>
              </div>
              <span style={{ ...g.provideTag, color: p.accent, borderColor: p.accent + '55' }}>{p.tag}</span>
              <p style={g.provideIntro}>{p.intro}</p>
              <ul style={g.provideList}>
                {p.points.map((pt, j) => (
                  <li key={j} style={g.provideItem}>
                    <span style={{ ...g.itemBar, background: p.accent }} />{pt}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}

const g = {
  page: {
    minHeight: '100vh', background: '#0d1117',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: '#e2e8f0',
  },
  wrap: { width: '90%', maxWidth: 1440, margin: '0 auto', padding: '30px 0 56px', boxSizing: 'border-box' },

  // 1. Hero
  hero: { textAlign: 'center', marginBottom: 26 },
  badge: {
    ...glassSurface('#0d9488', '#06b6d4', '#67E8F9'),
    display: 'inline-flex', alignItems: 'center', gap: 8, borderRadius: 20, padding: '7px 16px',
    marginBottom: 14,
    fontWeight: 800, fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase',
  },
  badgeIcon: { fontSize: 15 },
  title: { fontSize: 44, fontWeight: 900, color: '#fff', lineHeight: 1.08, margin: 0, letterSpacing: '-1px' },
  tagline: { fontSize: 19, fontWeight: 600, color: '#cbd5e1', margin: '12px auto 0', maxWidth: 760, lineHeight: 1.5 },

  // 2. Top hero card — download (left) + install guide (right)
  topBand: {
    position: 'relative', overflow: 'hidden', borderRadius: 18,
    border: '1px solid rgba(6,182,212,0.4)',
    background: 'linear-gradient(160deg, rgba(13,148,136,0.15) 0%, rgba(13,17,23,0.25) 62%)',
    boxShadow: '0 0 0 1px rgba(6,182,212,0.16), 0 12px 46px rgba(0,0,0,0.45)',
    display: 'grid', gridTemplateColumns: 'minmax(320px, 1fr) minmax(340px, 1.05fr)',
    marginBottom: 40, alignItems: 'start',
  },
  bandGlow: {
    position: 'absolute', top: -100, left: '20%', width: 520, height: 250, borderRadius: '50%',
    background: 'radial-gradient(ellipse, rgba(6,182,212,0.2) 0%, transparent 70%)', pointerEvents: 'none',
  },

  // Left column
  dlCol: { position: 'relative', zIndex: 1, padding: '30px 32px' },
  kicker: { fontSize: 12.5, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#5eead4', marginBottom: 12 },
  versionRow: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 },
  versionPill: {
    display: 'inline-block', padding: '6px 13px', borderRadius: 8,
    background: 'rgba(6,182,212,0.14)', border: '1px solid rgba(6,182,212,0.3)',
    color: '#a5f3fc', fontSize: 14, fontWeight: 700,
  },
  versionMeta: { fontSize: 14, color: '#94a3b8', fontWeight: 500 },
  ctaButton: {
    ...glassSurface('#0d9488', '#06b6d4', '#67E8F9'),
    display: 'block', width: '100%', boxSizing: 'border-box',
    borderRadius: 12, padding: '17px 26px', fontSize: 17.5, fontWeight: 800, cursor: 'pointer',
    letterSpacing: '-0.2px',
    transition: 'filter 0.15s ease',
  },
  ctaDisabled: {
    background: 'linear-gradient(135deg,#334155,#475569)', color: 'rgba(255,255,255,0.82)',
    cursor: 'default', boxShadow: '0 0 18px rgba(71,85,105,0.4), 0 8px 24px rgba(0,0,0,0.35)',
  },
  trustLine: { marginTop: 14, fontSize: 13.5, color: '#7c8a9c', lineHeight: 1.5 },
  lead: {
    margin: '18px 0 0', fontSize: 17, color: '#dbe4ee', lineHeight: 1.75,
    paddingTop: 18, borderTop: '1px solid rgba(255,255,255,0.08)',
  },
  nextTitle: { fontSize: 17, fontWeight: 800, color: '#fff', letterSpacing: '0.01em', margin: '20px 0 14px' },
  nextList: { display: 'flex', flexDirection: 'column', gap: 14 },
  nextItem: { display: 'flex', alignItems: 'center', gap: 13, fontSize: 16.5, color: '#dbe4ee', lineHeight: 1.5 },
  nextNum: {
    flexShrink: 0, width: 28, height: 28, borderRadius: '50%',
    background: 'rgba(6,182,212,0.16)', border: '1px solid rgba(6,182,212,0.35)', color: '#a5f3fc',
    fontSize: 14, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },

  // Right column — installation guide
  instCol: {
    position: 'relative', zIndex: 1, padding: '32px 34px',
    borderLeft: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.025)',
  },
  instHeading: { fontSize: 23, fontWeight: 800, color: '#fff', margin: '0 0 16px', letterSpacing: '-0.3px' },
  steps: { margin: '0 0 18px', paddingLeft: 22 },
  step: { fontSize: 15.5, color: '#e2e8f0', lineHeight: 1.8, marginBottom: 5 },
  instHelp: {
    display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap',
    paddingTop: 18, borderTop: '1px solid rgba(255,255,255,0.08)',
  },
  shot: {
    width: '100%', maxWidth: 260, height: 'auto', borderRadius: 9,
    border: '1px solid rgba(255,255,255,0.14)', boxShadow: '0 8px 22px rgba(0,0,0,0.45)', flexShrink: 0,
  },
  caption: { flex: 1, minWidth: 200, margin: 0, fontSize: 16, color: '#b4c0ce', lineHeight: 1.7 },

  // 3. Provides — dense product cards
  sectionHead: { fontSize: 27, fontWeight: 800, color: '#fff', textAlign: 'center', margin: '0 0 22px', letterSpacing: '-0.4px' },
  provideGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 },
  provideCard: {
    background: 'linear-gradient(180deg, rgba(255,255,255,0.045) 0%, rgba(13,17,23,0) 82%)',
    border: '1px solid rgba(255,255,255,0.12)', borderRadius: 16, padding: '26px 26px 24px',
    display: 'flex', flexDirection: 'column',
  },
  provideTop: { display: 'flex', alignItems: 'center', gap: 11, marginBottom: 12 },
  provideDot: { width: 13, height: 13, borderRadius: '50%', boxShadow: '0 0 10px rgba(255,255,255,0.18)', flexShrink: 0 },
  provideTitle: { fontSize: 20, fontWeight: 800, color: '#fff', letterSpacing: '-0.2px' },
  provideTag: {
    alignSelf: 'flex-start', fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
    border: '1px solid', borderRadius: 20, padding: '4px 11px', marginBottom: 14,
  },
  provideIntro: { fontSize: 15, color: '#cbd5e1', lineHeight: 1.65, margin: '0 0 16px' },
  provideList: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 11 },
  provideItem: { position: 'relative', paddingLeft: 18, fontSize: 14.5, color: '#94a3b8', lineHeight: 1.55 },
  itemBar: { position: 'absolute', left: 0, top: 8, width: 7, height: 7, borderRadius: 2, transform: 'rotate(45deg)' },
};
